import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Share2, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef, useState } from 'react';

import { usePublicProject } from '@/hooks/useProjects';
import { getErrorMessage } from '@/utils/error';
import { resolveImageUrl } from '@/utils/url';
import type { ContentBlockDTO, TextStyle } from '@/types/project';

/**
 * Collects all images from IMAGE_SLIDER and HERO_IMAGE content blocks
 * into a single flat array for the "Visual Interface" carousel.
 */
function collectCarouselImages(blocks: ContentBlockDTO[]): string[] {
  const images: string[] = [];
  for (const block of blocks) {
    if (block.type === 'IMAGE_SLIDER') {
      if (block.imageUrls) {
        images.push(...block.imageUrls);
      }
    }
  }
  return images;
}

/**
 * Returns Tailwind classes based on the textStyle of a TEXT block.
 */
function getTextStyleClasses(style?: TextStyle | null, bold?: boolean, italic?: boolean): string {
  const base = {
    H1: 'font-headline text-xl font-extrabold tracking-tight',
    H2: 'font-headline text-lg font-bold tracking-tight',
    H3: 'font-headline text-base font-bold',
    H4: 'font-headline text-sm font-semibold',
    P: 'text-sm',
  }[style || 'P'];

  return `${base} ${bold ? 'font-bold' : ''} ${italic ? 'italic' : ''}`.trim();
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ? Number(id) : undefined;
  const { data: project, isLoading, error } = usePublicProject(projectId);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showToast, setShowToast] = useState(false);

  const handleShare = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    const scrollAmount = 300;
    carouselRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-background">
        <Loader2 className="h-8 w-8 animate-spin text-dark-primary" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-dark-background font-sans text-dark-on-surface">
        <p className="text-error">{error ? getErrorMessage(error) : 'Project not found'}</p>
        <Link
          to="/"
          className="text-sm text-dark-secondary transition-colors hover:text-dark-primary"
        >
          Back to Home
        </Link>
      </div>
    );
  }

  const sortedBlocks = [...project.contentBlocks].sort((a, b) => a.order - b.order);
  const carouselImages = collectCarouselImages(sortedBlocks);

  return (
    <div className="min-h-screen bg-dark-background font-sans text-dark-on-surface selection:bg-dark-secondary-container/30">
      {/* ── Header ── */}
      <header className="fixed top-0 z-50 flex w-full items-center justify-between bg-[#111319]/80 px-6 py-4 shadow-[0_40px_40px_-10px_rgba(0,26,64,0.4)] backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-3">
          <ArrowLeft className="h-5 w-5 text-dark-secondary-container" />
          <span className="font-headline text-xl font-black tracking-tight text-dark-on-surface">
            Portfolio
          </span>
        </Link>
        <div className="flex items-center gap-2 text-dark-on-surface/70">
          <button
            onClick={handleShare}
            className="rounded-full p-2 transition-colors hover:bg-dark-surface-container-highest"
          >
            <Share2 className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 pb-32 pt-24 md:px-8">
        {/* ── Hero Section ── */}
        <section className="mb-10 flex flex-col items-start gap-6 md:flex-row md:items-center">
          {/* App Icon — thumbnailUrl (2/3 of original size) */}
          <div className="relative h-[4.67rem] w-[4.67rem] shrink-0 overflow-hidden rounded-[10px] shadow-2xl md:h-[6.67rem] md:w-[6.67rem]">
            {project.thumbnailUrl ? (
              <img
                src={resolveImageUrl(project.thumbnailUrl) || ''}
                alt={project.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-dark-surface-container">
                <span className="text-2xl font-black text-dark-outline md:text-3xl">
                  {project.name.charAt(0)}
                </span>
              </div>
            )}
          </div>

          {/* Project Info */}
          <div className="flex-grow space-y-2">
            <div className="space-y-0.5">
              <h1 className="font-headline text-2xl font-extrabold tracking-tight text-dark-on-surface md:text-4xl">
                {project.name}
              </h1>
              {project.roleSpecification && (
                <p className="text-sm font-semibold uppercase tracking-wide text-dark-secondary">
                  {project.roleSpecification}
                </p>
              )}
            </div>

            {/* Download / Link buttons */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              {project.androidLink && (
                <a
                  href={project.androidLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 active:scale-95"
                >
                  <svg className="h-12 w-auto" viewBox="0 0 180 53" xmlns="http://www.w3.org/2000/svg">
                    <rect width="180" height="53" rx="17.7" fill="#000" />
                    {/* Google Play triangle logo */}
                    <path d="M18.8 11.1c-.2.3-.3.8-.3 1.3v28.1c0 .5.1 1 .3 1.3l.1.1 15.6-15.6v-.4L18.9 11z" fill="#3bccff" />
                    <path d="M40.2 31l-5.7-5.7v-.4l5.7-5.7.1.1 6.8 3.9c2 1.1 2 2.9 0 4.1l-6.9 3.8z" fill="#ffd400" />
                    <path d="M34.5 25.9L18.8 41.8c.7.7 1.7.7 2.9 0L41.3 31l-6.8-5.1z" fill="#ff3333" />
                    <path d="M34.5 25.9l6.8-4.8L21.7 11c-1.2-.7-2.3-.7-2.9 0l15.7 14.9z" fill="#48ff48" />
                    {/* Text: Download Google Play */}
                    <text x="56" y="22" fill="#fff" fontSize="9" fontFamily="Inter, sans-serif" fontWeight="400">Download</text>
                    <text x="56" y="38" fill="#fff" fontSize="14" fontFamily="Inter, sans-serif" fontWeight="700">Google Play</text>
                  </svg>
                </a>
              )}
              {project.iosLink && (
                <a
                  href={project.iosLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 active:scale-95"
                >
                  <svg className="h-12 w-auto" viewBox="0 0 180 53" xmlns="http://www.w3.org/2000/svg">
                    <rect width="180" height="53" rx="17.7" fill="#000" />
                    {/* Apple logo */}
                    <path d="M35.6 26.7c0-4.4 3.6-6.8 3.7-6.9-2-2.9-5.2-3.3-6.4-3.5-2.8-.3-5.5 1.6-6.9 1.6-1.3 0-3.6-1.6-5.9-1.6-3.1 0-5.9 1.7-7.3 4.4-3.2 5.6-.8 13.7 2.3 18.1 1.5 2.1 3.2 4.5 5.6 4.5 2.3 0 3.1-1.3 5.9-1.3 2.7 0 3.5 1.3 5.9 1.3 2.4 0 4-2.1 5.5-4.3 1.1-1.6 1.9-3.3 2.3-3.9-.1 0-4.7-1.8-4.7-7.4zm-5.7-13.8c1.2-1.5 2.1-3.6 1.9-5.6-1.7.1-3.9 1.2-5.1 2.7-1.1 1.3-2 3.3-1.7 5.5 2 .1 3.7-1.1 4.9-2.6z" fill="#fff" />
                    {/* Text: Download Apple Store */}
                    <text x="56" y="22" fill="#fff" fontSize="9" fontFamily="Inter, sans-serif" fontWeight="400">Download</text>
                    <text x="56" y="38" fill="#fff" fontSize="14" fontFamily="Inter, sans-serif" fontWeight="700">Apple Store</text>
                  </svg>
                </a>
              )}
              {project.webLink && (
                <a
                  href={project.webLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-full bg-gradient-to-br from-dark-primary-container to-dark-secondary-container px-6 py-2.5 font-headline text-sm font-extrabold text-white shadow-[0_20px_40px_-10px_rgba(0,162,253,0.3)] transition-all hover:scale-[1.02] active:scale-95"
                >
                  View Live Project
                  <ArrowRight className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </section>

        {/* ── Visual Interface — IMAGE_SLIDER images ── */}
        {carouselImages.length > 0 && (
          <section className="mb-10">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-headline text-base font-bold text-dark-on-surface">
                Visual Interface
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => scrollCarousel('left')}
                  className="rounded-full bg-dark-surface-container p-1.5 text-dark-outline transition-colors hover:text-dark-on-surface"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => scrollCarousel('right')}
                  className="rounded-full bg-dark-surface-container p-1.5 text-dark-outline transition-colors hover:text-dark-on-surface"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div
              ref={carouselRef}
              className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-4"
              style={{ scrollbarWidth: 'none' }}
            >
              {carouselImages.map((url, i) => (
                <div
                  key={i}
                  className="group shrink-0 h-[400px] cursor-pointer overflow-hidden rounded-xl border border-dark-outline-variant/10 bg-dark-surface-container-low shadow-2xl"
                >
                  <img
                    src={resolveImageUrl(url) || ''}
                    alt={`Screenshot ${i + 1}`}
                    className="h-full w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Main Grid: Content + Sidebar ── */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column — All content blocks in order */}
          <div className="space-y-5 lg:col-span-2">
            {sortedBlocks
              .filter((b) => b.type === 'TEXT' || b.type === 'HERO_IMAGE')
              .map((block, index) => {
                if (block.type === 'TEXT') {
                  return (
                    <section key={block.id ?? index}>
                      {block.heading && (
                        <h2 className="mb-3 flex items-center gap-2 font-headline text-base font-bold text-dark-on-surface">
                          {block.heading}
                          <div className="ml-3 h-px flex-grow bg-dark-outline-variant/20" />
                        </h2>
                      )}
                      {block.body && (
                        <div
                          className={`max-w-none leading-relaxed text-dark-on-surface-variant ${getTextStyleClasses(block.textStyle, block.bold, block.italic)}`}
                        >
                          {block.body.split('\n').map((paragraph, pIdx) => (
                            <p key={pIdx} className={pIdx > 0 ? 'mt-2' : ''}>
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      )}
                    </section>
                  );
                }

                // HERO_IMAGE
                const imageUrl = block.imageUrls?.[0];
                if (!imageUrl) return null;
                return (
                  <section key={block.id ?? `hero-${index}`} className="space-y-2">
                    {block.heading && (
                      <h3 className="font-headline text-base font-bold text-dark-on-surface">
                        {block.heading}
                      </h3>
                    )}
                    <div className="overflow-hidden rounded-xl">
                      <img
                        src={resolveImageUrl(imageUrl) || ''}
                        alt={block.caption || block.heading || 'Project image'}
                        className="w-full object-contain"
                      />
                    </div>
                    {block.caption && (
                      <p className="text-[10px] tracking-wide text-dark-outline">
                        {block.caption}
                      </p>
                    )}
                  </section>
                );
              })}

            {sortedBlocks.filter((b) => b.type === 'TEXT' || b.type === 'HERO_IMAGE').length ===
              0 &&
              carouselImages.length === 0 && (
                <p className="py-8 text-center text-sm text-dark-on-surface-variant">
                  No content available yet.
                </p>
              )}
          </div>

          {/* Right Column — Sidebar */}
          <aside className="space-y-5">
            {/* Technical Specs */}
            {project.technicalSpecs.length > 0 && (
              <section className="rounded-3xl border border-dark-outline-variant/10 bg-dark-surface-container p-6">
                <h3 className="mb-4 font-headline text-sm font-extrabold tracking-tight">
                  Technical Specs
                </h3>
                <div className="space-y-4">
                  {project.technicalSpecs.map((spec, i) => (
                    <div
                      key={spec.id ?? spec.key}
                      className={`flex items-center justify-between py-1.5 ${
                        i < project.technicalSpecs.length - 1
                          ? 'border-b border-dark-outline-variant/10'
                          : ''
                      }`}
                    >
                      <span className="text-xs uppercase tracking-widest text-dark-outline">
                        {spec.key}
                      </span>
                      <span className="text-sm font-semibold text-dark-on-surface">
                        {spec.value}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </aside>
        </div>
      </main>

      {/* Toast */}
      <div
        className={`fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-gradient-to-br from-dark-primary-container to-dark-secondary-container px-6 py-3 text-sm font-medium text-white shadow-[0_20px_40px_-10px_rgba(0,26,64,0.4)] transition-all duration-300 ${showToast ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 pointer-events-none'}`}
      >
        Link copied to clipboard
      </div>
    </div>
  );
}
